import React, {useCallback, useEffect, useRef, useState} from "react"
import {useSearchParams} from "react-router-dom"
import {HeaderComponentProps, PaginatedSelector} from "@/components/paginated-selector/paginated-selector";
import {apiV2} from "@/api/axiosConfig";
import {convertV1ToV2, PaginatedResponse} from "@/types/PaginatedResponse";
import {FilterQueryParams} from "@/components/paginated-selector/filters/types";
import {IncidentFeedFilter} from "@/components/paginated-selector/filters/IncidentFeedFilter";
import {IncidentTypeTagFilter} from "@/components/paginated-selector/filters/IncidentTypeTagFilter";
import {CountryLocationFilter} from "@/components/paginated-selector/filters/CountryLocationFilter";
import {SeverityFilter} from "@/components/paginated-selector/filters/SeverityFilter";
import {DateRangeFilter} from "@/components/paginated-selector/filters/DateRangeFilter";
import {FeedName} from "@/components/data-display/FeedName";
import {CountryName} from "@/components/data-display/CountryName";
import {IncidentTypeDisplay} from "@/components/data-display/IncidentTypeDisplay";
import {IncidentSeverityDisplay} from "@/components/incident-severity/IncidentSeverityDisplay";
import {IncidentDetailsModal} from "@/components/modals/IncidentDetailsModal";
import {useExportToXlsx} from "@/components/paginated-selector/useExportToXlsx";
import DownloadIcon from "@mui/icons-material/Download";

type Incident = {
    id: string;
    categoryName: string;
    typeName: string;
    severity: string;
    summary: string;
    description: string;
    countryISO: string;
    town?: string;
    feedID: string;
    tags: string[];
    startedAt: string;
    endedAt?: string;
    ongoing: boolean;
    reportedAt: string;
    updatedAt: string;
    source?: string;
    position?: {
        type: string;
        coordinates: [number, number];
    };
    references?: Array<{
        title?: string;
        url?: string;
    }>;
}

type IncidentSelectorProps = {
    renderActions?: (incident: Incident) => React.ReactNode;
    onColumnClick?: (incident: Incident, columnIndex: number) => void;
}

function loadStoredFeed(): FilterQueryParams {
    try {
        const stored = localStorage.getItem("incidentFeedSelection");
        if (stored) {
            const feed = JSON.parse(stored);
            if (feed.feedIDs?.length) {
                return {feedIDs: feed.feedIDs, feedNames: feed.feedNames, feedType: feed.feedType};
            }
        }
    } catch {}
    return {};
}

export function IncidentSelector({renderActions, onColumnClick}: IncidentSelectorProps) {
    const [selectedIncident, setSelectedIncident] = useState<Incident | null>(null);
    const [modalOpen, setModalOpen] = useState(false);
    const [searchParams, setSearchParams] = useSearchParams();
    const [filterParams, setFilterParams] = useState<FilterQueryParams>({});
    const filterOnChangeRef = useRef<((params: FilterQueryParams) => void) | null>(null);
    const initialFilterParams = useRef(loadStoredFeed());

    // Preload modal if incidentID is in query params on mount only
    useEffect(() => {
        const incidentID = searchParams.get("incidentID");
        if (!incidentID) return;

        apiV2.get<Incident>(`incidents/${incidentID}`).then(res => {
            setSelectedIncident(res.data);
            setModalOpen(true);
        }).catch((e) => {
            console.error("Failed to preload incident:", e);
        });
    }, []);

    const openModal = useCallback((incident: Incident) => {
        setSelectedIncident(incident);
        setModalOpen(true);
        setSearchParams(prev => {
            const params = new URLSearchParams(prev);
            params.set("incidentID", incident.id);
            return params;
        }, { replace: true });
    }, [setSearchParams]);

    const closeModal = useCallback((open: boolean) => {
        setModalOpen(open);
        if (!open) {
            setSearchParams(prev => {
                const params = new URLSearchParams(prev);
                params.delete("incidentID");
                return params;
            }, { replace: true });
        }
    }, [setSearchParams]);

    const isSamdesk = filterParams.feedType === "samdesk";

    const headers = isSamdesk
        ? ["Date", "Type/Tags", "Summary", "Location", "Feed"]
        : ["Date", "Type/Tags", "Severity", "Summary", "Location", "Feed"];

    const formatDate = useCallback((dateString: string) => {
        if (!dateString) return "N/A";
        const date = new Date(dateString);
        return date.toLocaleDateString() + " " + date.toLocaleTimeString([], {hour: "2-digit", minute:"2-digit"});
    }, []);

    const getIncidents = useCallback(async (search: string, page: number, filterParams?: FilterQueryParams) => {
        const params: any = {
            search: search,
            page: page,
            limit: 10,
            ...filterParams,
        }

        const res = await apiV2.get<PaginatedResponse<any>>("incidents", {params})

        return convertV1ToV2(res.data, 10)
    }, [])

    const {exportToXlsx, isExporting, progress} = useExportToXlsx({
        getItems: getIncidents,
        filename: "incidents",
        exportPageSize: 500, // Fetch 500 incidents per page during export
        columns: [
            { header: "Date", getValue: (inc: Incident) => inc.startedAt ? formatDate(inc.startedAt) : formatDate(inc.reportedAt) },
            { header: "Category", getValue: (inc: Incident) => inc.categoryName },
            { header: "Type", getValue: (inc: Incident) => inc.typeName || "" },
            { header: "Tags", getValue: (inc: Incident) => inc.tags?.join(", ") || "" },
            { header: "Severity", getValue: (inc: Incident) => inc.severity },
            { header: "Summary", getValue: (inc: Incident) => inc.summary || "" },
            { header: "Description", getValue: (inc: Incident) => inc.description || "" },
            { header: "Country", getValue: (inc: Incident) => inc.countryISO },
            { header: "Town", getValue: (inc: Incident) => inc.town || "" },
            { header: "Latitude", getValue: (inc: Incident) => inc.position?.coordinates?.[1] || "" },
            { header: "Longitude", getValue: (inc: Incident) => inc.position?.coordinates?.[0] || "" },
            { header: "Source", getValue: (inc: Incident) => inc.source || "" },
            { header: "Started At", getValue: (inc: Incident) => inc.startedAt },
            { header: "Ended At", getValue: (inc: Incident) => inc.endedAt || "" },
            { header: "Ongoing", getValue: (inc: Incident) => inc.ongoing ? "Yes" : "No" },
        ]
    });

    const renderHeader = useCallback((props: HeaderComponentProps) => {
        // Capture the onChange for column 1 (Type/Tags) so tag chips can trigger it
        if (props.columnIndex === 1) {
            filterOnChangeRef.current = props.onChange;
        }
        if (props.columnIndex === 0) return <DateRangeFilter {...props} />;
        if (props.columnIndex === 1) return <IncidentTypeTagFilter {...props} />;
        if (!isSamdesk) {
            if (props.columnIndex === 2) return <SeverityFilter {...props} />;
            if (props.columnIndex === 4) return <CountryLocationFilter {...props} />;
            if (props.columnIndex === 5) return <IncidentFeedFilter {...props} />;
        } else {
            if (props.columnIndex === 3) return <CountryLocationFilter {...props} />;
            if (props.columnIndex === 4) return <IncidentFeedFilter {...props} />;
        }
        return props.label;
    }, [isSamdesk]);

    const formatTagLabel = useCallback((tag: string) => {
        return tag
            .split("_")
            .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
            .join(" ");
    }, []);

    const handleTagClick = useCallback((tag: string) => {
        filterOnChangeRef.current?.({
            filterMode: "tags",
            tags: [tag],
            tagLabels: [formatTagLabel(tag)],
            excludedTags: undefined,
            excludedTagLabels: undefined,
            tagMode: "any",
            categoryTypeIDs: undefined,
        });
    }, [formatTagLabel]);

    const getColumns = useCallback((incident: Incident) => {
        const cols: React.ReactNode[] = [
            formatDate(incident.startedAt ? incident.startedAt : incident.reportedAt),
            <IncidentTypeDisplay
                key={`type-${incident.id}`}
                categoryName={incident.categoryName}
                typeName={incident.typeName}
                tags={incident.tags}
                onTagClick={handleTagClick}
            />,
        ];
        if (!isSamdesk) {
            cols.push(
                <IncidentSeverityDisplay
                    key={`severity-${incident.id}`}
                    severity={incident.severity}
                    tags={incident.tags}
                />
            );
        }
        cols.push(
            incident.summary?.substring(0, 100) || "No summary",
            <CountryName key={`country-${incident.id}`} countryISO={incident.countryISO} />,
            <FeedName key={`feed-${incident.id}`} feedID={incident.feedID} />
        );
        return cols;
    }, [formatDate, isSamdesk, handleTagClick])

    const handleColumnClick = useCallback((incident: Incident, columnIndex: number) => {
        if (onColumnClick) {
            onColumnClick(incident, columnIndex);
        } else {
            openModal(incident);
        }
    }, [onColumnClick, openModal]);

    const renderTopBarActions = useCallback((search: string, filterParams: FilterQueryParams) => {
        setFilterParams(filterParams);
        try {
            localStorage.setItem("incidentFeedSelection", JSON.stringify({
                feedIDs: filterParams.feedIDs ?? [],
                feedNames: filterParams.feedNames ?? [],
                feedType: filterParams.feedType ?? "",
            }));
        } catch {}

        const percentage = progress ? Math.round((progress.current / progress.total) * 100) : 0;

        return (
            <div className="flex flex-col gap-2 items-end w-full">
                <button
                    onClick={() => exportToXlsx(search, filterParams)}
                    disabled={isExporting}
                    className="flex items-center gap-2 px-4 py-2 bg-blue text-white rounded hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition-opacity"
                >
                    <DownloadIcon fontSize="small" />
                    <span className="font-medium">
                        {isExporting ? "Exporting..." : "Export to Excel"}
                    </span>
                </button>
                {isExporting && progress && (
                    <div className="w-full">
                        <div className="flex justify-between text-xs text-grey mb-1">
                            <span>Progress</span>
                            <span>{percentage}%</span>
                        </div>
                        <div className="w-full bg-light-light-grey rounded-full h-2 overflow-hidden">
                            <div
                                className="bg-blue h-full transition-all duration-300"
                                style={{ width: `${percentage}%` }}
                            />
                        </div>
                    </div>
                )}
            </div>
        );
    }, [exportToXlsx, isExporting, progress]);

    return (
        <>
            <PaginatedSelector
                headers={headers}
                getItems={getIncidents}
                getColumns={getColumns}
                disableSelect={true}
                hideSearch={true}
                renderActions={renderActions}
                onColumnClick={handleColumnClick}
                renderHeader={renderHeader}
                syncWithUrl={true}
                renderTopBarActions={renderTopBarActions}
                initialFilterParams={initialFilterParams.current}
            />

            <IncidentDetailsModal
                incident={selectedIncident}
                open={modalOpen}
                setOpen={closeModal}
                onTagClick={handleTagClick}
            />
        </>
    );
}
